import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useAuth } from "@bw/shared/hooks";
import type { PostCreate } from "@bw/shared/types";
import { renderMarkdown } from "@bw/shared/utils";
import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { useCreatePersonalPost } from "../hooks/useCreatePersonalPost";
import { MAX_MEDIA_PER_POST, uploadPostPhoto } from "../lib/photoUpload";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

type ComposerTab = "write" | "preview";

export interface PostComposerProps {
  trigger?: "row" | "icon";
}

export function PostComposer({ trigger = "row" }: PostComposerProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  if (!user) return null;

  const close = (): void => {
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <>
      {trigger === "icon" ? (
        <Tooltip title="Start a post">
          <IconButton
            ref={triggerRef}
            type="button"
            color="primary"
            size="small"
            aria-label="Start a post"
            onClick={() => setOpen(true)}
          >
            <AddRoundedIcon aria-hidden="true" />
          </IconButton>
        </Tooltip>
      ) : (
        <Paper variant="outlined">
          <ButtonBase
            ref={triggerRef}
            type="button"
            aria-label="Start a post — Share a match, a highlight…"
            title="Start a post"
            onClick={() => setOpen(true)}
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              gap: 1.5,
              px: 2,
              py: 1.25,
              textAlign: "left",
            }}
          >
            <Avatar
              src={user.photoURL ?? undefined}
              alt=""
              aria-hidden
              sx={{ width: 36, height: 36 }}
            >
              {user.displayName?.trim().charAt(0).toUpperCase() || "?"}
            </Avatar>
            <Typography color="text.secondary" sx={{ flex: 1 }}>
              Share a match, a highlight…
            </Typography>
            <EditOutlinedIcon color="primary" aria-hidden />
          </ButtonBase>
        </Paper>
      )}
      <PostComposerDialog open={open} onClose={close} />
    </>
  );
}

interface PendingPhoto {
  id: string;
  publicUrl: string;
  objectKey: string;
}

function PostComposerDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreatePersonalPost();
  const [content, setContent] = useState("");
  const [tab, setTab] = useState<ComposerTab>("write");
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const photoSeqRef = useRef(0);
  const writeTabId = useId();
  const previewTabId = useId();
  const writePanelId = useId();
  const previewPanelId = useId();

  const trimmed = content.trim();
  const atCapacity = photos.length >= MAX_MEDIA_PER_POST;
  const canSubmit =
    (trimmed.length > 0 || photos.length > 0) &&
    !create.isPending &&
    !uploading;

  async function handleFileSelected(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file || atCapacity || uploading) return;

    setUploadError(false);
    setUploading(true);
    try {
      const uploaded = await uploadPostPhoto(file);
      const photoId = `photo-${photoSeqRef.current++}`;
      setPhotos((previous) =>
        previous.length >= MAX_MEDIA_PER_POST
          ? previous
          : [
              ...previous,
              {
                id: photoId,
                publicUrl: uploaded.publicUrl,
                objectKey: uploaded.objectKey,
              },
            ],
      );
    } catch {
      setUploadError(true);
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSubmit || create.isPending || uploading) return;
    const data: PostCreate = {
      content: trimmed,
      media_urls: photos.map((photo) => photo.publicUrl),
    };
    create.mutate(data, { onSuccess: () => onClose() });
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={create.isPending ? undefined : onClose}
      aria-labelledby="new-post-title"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle id="new-post-title" sx={{ pr: 7 }}>
          New post
        </DialogTitle>
        <IconButton
          type="button"
          aria-label="Close"
          title="Close"
          onClick={onClose}
          disabled={create.isPending}
          sx={{ position: "absolute", top: 10, right: 10 }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <DialogContent dividers>
          <Stack spacing={2}>
            {create.isError || uploadError ? (
              <GuardianAwareError error={uploadError ? null : create.error}>
                <Alert severity="error" role="alert">
                  {uploadError
                    ? "We couldn't upload that photo. Please try again."
                    : "We couldn't share your post. Please try again."}
                </Alert>
              </GuardianAwareError>
            ) : null}

            <Tabs
              value={tab}
              onChange={(_event, value: ComposerTab) => setTab(value)}
              aria-label="Compose"
            >
              <Tab
                value="write"
                id={writeTabId}
                aria-controls={writePanelId}
                label="Write"
              />
              <Tab
                value="preview"
                id={previewTabId}
                aria-controls={previewPanelId}
                label="Preview"
              />
            </Tabs>

            {tab === "write" ? (
              <Box
                id={writePanelId}
                role="tabpanel"
                aria-labelledby={writeTabId}
              >
                <TextField
                  autoFocus
                  id="post-composer-content"
                  label="What's on your mind?"
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  multiline
                  minRows={5}
                  fullWidth
                />
              </Box>
            ) : (
              <Paper
                id={previewPanelId}
                role="tabpanel"
                aria-labelledby={previewTabId}
                aria-label="Post preview"
                variant="outlined"
                sx={{ minHeight: 144, p: 2 }}
              >
                {trimmed.length > 0 ? (
                  renderMarkdown(content)
                ) : (
                  <Typography color="text.secondary">
                    Nothing to preview yet.
                  </Typography>
                )}
              </Paper>
            )}

            <Stack spacing={1.5}>
              {photos.length > 0 ? (
                <ImageList cols={3} gap={8} sx={{ m: 0 }}>
                  {photos.map((photo) => (
                    <ImageListItem key={photo.id}>
                      <Box
                        component="img"
                        src={photo.publicUrl}
                        alt="Uploaded image"
                        sx={{
                          width: "100%",
                          aspectRatio: "1",
                          objectFit: "cover",
                          borderRadius: 1,
                        }}
                      />
                      <IconButton
                        type="button"
                        size="small"
                        aria-label="Remove image"
                        title="Remove image"
                        onClick={() =>
                          setPhotos((previous) =>
                            previous.filter((item) => item.id !== photo.id),
                          )
                        }
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "background.paper",
                        }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : null}
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  disabled={atCapacity || uploading}
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                >
                  {uploading ? "Uploading…" : "Add photo"}
                  <Box
                    component="input"
                    id="post-composer-photo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelected}
                    disabled={atCapacity || uploading}
                    sx={{ display: "none" }}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {photos.length}/{MAX_MEDIA_PER_POST}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            Publish
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
